import toPairs from 'lodash/toPairs';
import { types as sdkTypes } from './sdkLoader';
import { diffInTime } from './dates';

const { LatLng, Money } = sdkTypes;

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

export const TELEPHONE_MIN_LENGTH = 9;

const isNonEmptyString = val => {
  return typeof val === 'string' && val.trim().length > 0;
};

/**
 * Validator functions and helpers for Final Forms
 */

// Final Form expects and undefined value for a successful validation
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return message;
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : message;
  }
  return VALID;
};

export const requiredStringNoTrim = message => value => {
  return typeof value === 'string' && value.length > 0 ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredBoolean = message => value => {
  return typeof value === 'boolean' ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredAndNonEmptyString = message => value => {
  return isNonEmptyString(value) ? VALID : message;
};

export const requiredFieldArrayCheckbox = message => value => {
  if (!value) {
    return message;
  }

  const entries = toPairs(value);
  const hasSelectedValues = entries.filter(e => !!e[1]).length > 0;
  return hasSelectedValues ? VALID : message;
};

export const minLength = (message, minimumLength) => value => {
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length >= minimumLength ? VALID : message;
};

export const maxLength = (message, maximumLength) => value => {
  if (!value) {
    return VALID;
  }
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length <= maximumLength ? VALID : message;
};

export const nonEmptyArray = message => value => {
  return value && Array.isArray(value) && value.length > 0 ? VALID : message;
};

export const autocompleteSearchRequired = message => value => {
  return value && value.search ? VALID : message;
};

export const autocompletePlaceSelected = message => value => {
  const selectedPlaceIsValid =
    value &&
    value.selectedPlace &&
    value.selectedPlace.address &&
    value.selectedPlace.origin instanceof LatLng;
  return selectedPlaceIsValid ? VALID : message;
};

export const bookingDateRequired = inValidDateMessage => value => {
  const dateIsValid = value && value.date instanceof Date;
  return !dateIsValid ? inValidDateMessage : VALID;
};

export const bookingDatesRequired = (inValidStartDateMessage, inValidEndDateMessage) => value => {
  const startDateIsValid = value && value.startDate instanceof Date;
  const endDateIsValid = value && value.endDate instanceof Date;

  if (!startDateIsValid) {
    return inValidStartDateMessage;
  } else if (!endDateIsValid) {
    return inValidEndDateMessage;
  } else {
    return VALID;
  }
};

// Source: http://www.regular-expressions.info/email.html
// See the link above for an explanation of the tradeoffs.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const emailFormatValid = message => value => {
  return value && EMAIL_RE.test(value) ? VALID : message;
};

const DATE_RE = /^(0[1-9]|[1-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/i;
export const dateValid = message => value => {
  return value && DATE_RE.test(value) ? VALID : message;
};

export const moneySubUnitAmountAtLeast = (message, minValue) => value => {
  return value instanceof Money && value.amount >= minValue ? VALID : message;
};

/** Importe en subunidades estrictamente mayor que cero (p. ej. precio /h con checkbox activo) */
export const moneyMustBePositiveSubunits = message => value => {
  if (value == null || value === '') {
    return VALID;
  }
  return value instanceof Money && Number(value.amount) > 0 ? VALID : message;
};

const parseNum = str => {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
};

export const numberAtLeast = (message, minNumber) => value => {
  const valueNum = parseNum(value);
  return typeof valueNum === 'number' && valueNum >= minNumber ? VALID : message;
};

export const numberAtMax = (message, maxNumber) => value => {
  const valueNum = parseNum(value);
  return typeof valueNum === 'number' && valueNum <= maxNumber ? VALID : message;
};

export const ageAtLeast = (message, minYears) => value => {
  const { year, month, day } = value;
  const dayNum = parseNum(day);
  const monthNum = parseNum(month);
  const yearNum = parseNum(year);

  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = new Date();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = diffInTime(now, age, 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

export const validBusinessURL = message => value => {
  if (typeof value === 'undefined' || value === null) {
    return message;
  }

  const disallowedChars = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/;
  const protocolTokens = value.split(':');
  const includesProtocol = protocolTokens.length > 1;
  const usesHttpProtocol = includesProtocol && !!protocolTokens[0].match(/^(https?)/);

  const invalidCharacters = !!value.match(disallowedChars);
  const invalidProtocol = !(usesHttpProtocol || !includesProtocol);
  // Stripe checks against example.com
  const isExampleDotCom = !!value.match(/^(https?:\/\/example\.com|example\.com)/);
  const isLocalhost = !!value.match(/^(https?:\/\/localhost($|:|\/)|localhost($|:|\/))/);
  return invalidCharacters || invalidProtocol || isExampleDotCom || isLocalhost ? message : VALID;
};

export const validSsnLast4 = message => value => {
  return value.length === 4 ? VALID : message;
};

// Spanish tax identifiers: NIF (individuals), NIE (foreign residents) and CIF
// (legal entities). Each carries a check character, so a typo in an invoice's
// tax id is catchable here rather than at the Hacienda's expense.
const NIF_CHECK_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
const CIF_CHECK_LETTERS = 'JABCDEFGHI';
// Entity classes whose check character is always a letter (N,P,Q,R,S,W) or
// always a digit (A,B,E,H). The rest accept either.
const CIF_LETTER_ONLY = 'NPQRSW';
const CIF_DIGIT_ONLY = 'ABEH';

const nifCheckLetterFor = number => NIF_CHECK_LETTERS.charAt(number % 23);

const isValidNif = value => {
  // Plain NIF (8 digits + letter) and the K/L/M special forms (letter + 7 digits
  // + letter), which share the mod-23 table.
  const match = value.match(/^([KLM]?)(\d{7,8})([A-Z])$/);
  if (!match) {
    return false;
  }
  const [, prefix, digits, checkLetter] = match;
  if (!prefix && digits.length !== 8) {
    return false;
  }
  if (prefix && digits.length !== 7) {
    return false;
  }
  return nifCheckLetterFor(parseInt(digits, 10)) === checkLetter;
};

const isValidNie = value => {
  const match = value.match(/^([XYZ])(\d{7})([A-Z])$/);
  if (!match) {
    return false;
  }
  const [, prefix, digits, checkLetter] = match;
  // X/Y/Z stand in for a leading 0/1/2, then it is a NIF.
  const asNumber = parseInt(`${'XYZ'.indexOf(prefix)}${digits}`, 10);
  return nifCheckLetterFor(asNumber) === checkLetter;
};

const isValidCif = value => {
  const match = value.match(/^([ABCDEFGHJNPQRSUVW])(\d{7})([0-9A-J])$/);
  if (!match) {
    return false;
  }
  const [, entityLetter, digits, checkChar] = match;

  // Odd positions (1st, 3rd, 5th, 7th) are doubled and their digits summed;
  // even positions are added as they are.
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    const digit = parseInt(digits.charAt(i), 10);
    if (i % 2 === 0) {
      const doubled = digit * 2;
      sum += Math.floor(doubled / 10) + (doubled % 10);
    } else {
      sum += digit;
    }
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  const expectedLetter = CIF_CHECK_LETTERS.charAt(checkDigit);

  if (CIF_LETTER_ONLY.includes(entityLetter)) {
    return checkChar === expectedLetter;
  }
  if (CIF_DIGIT_ONLY.includes(entityLetter)) {
    return checkChar === String(checkDigit);
  }
  return checkChar === String(checkDigit) || checkChar === expectedLetter;
};

/**
 * Validate a Spanish NIF, NIE or CIF by its check character.
 *
 * Only meaningful for Spanish tax ids — callers apply it when the billing
 * country is ES and fall back to `required` elsewhere.
 */
export const validSpanishTaxId = message => value => {
  const normalized = (value || '')
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[\s.-]/g, '');

  if (!normalized) {
    return message;
  }
  return isValidNif(normalized) || isValidNie(normalized) || isValidCif(normalized)
    ? VALID
    : message;
};

/**
 * `validSpanishTaxId`, salvo que el usuario haya dicho que su documento no es
 * español (un pasaporte, un DNI de otro país), que no tiene dígito de control
 * que comprobar.
 *
 * Se decide con `allValues` y no poniendo o quitando el validador según la
 * casilla: final-form valida al cambiar la casilla, ANTES del re-render, con el
 * validador que hubiera, y el error se quedaría pegado.
 *
 * @param {string} message
 * @param {Function} isForeign allValues => boolean
 */
export const validSpanishTaxIdUnless = (message, isForeign) => (value, allValues) =>
  isForeign(allValues || {}) ? VALID : validSpanishTaxId(message)(value);

export const validHKID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  // HKID format example: AB364912(5)
  // ID can start with one or two letters and the check digit in the end can be in brackets or not
  if (value.length < 8) {
    return message;
  }

  // Handle possible brackets in value
  if (value.charAt(value.length - 3) === '(' && value.charAt(value.length - 1) === ')') {
    value = value.substring(0, value.length - 3) + value.charAt(value.length - 2);
  }
  value = value.toUpperCase();

  // Check that pattern is correct and split value to array
  const hkidPattern = /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
  const matchArray = value.match(hkidPattern);

  if (!matchArray) {
    return message;
  }

  const charPart = matchArray[1];
  const numPart = matchArray[2];
  const checkDigit = matchArray[3];

  // Calculate the checksum for character part.
  // Transfer letters to numbers so that A=10, B=11, C=12 etc.
  // If there is only one letter in the ID use 36 as the first value
  // Total calculation is weighted so that 1st digit is x9, 2nd digit x8, 3rd digit x7 etc.

  const strValidChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let checkSum = 0;

  if (charPart.length === 2) {
    checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
    checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
  } else {
    checkSum += 9 * 36;
    checkSum += 8 * (10 + strValidChars.indexOf(charPart));
  }

  // Calculate the checksum for numeric part

  for (let i = 0, j = 7; i < numPart.length; i++, j--) {
    checkSum += j * numPart.charAt(i);
  }

  // Verify the check digit
  const remaining = checkSum % 11;
  let verify = remaining === 0 ? 0 : 11 - remaining;
  verify = verify.toString();
  const isValid = verify === checkDigit || (verify === 10 && checkDigit === 'A');

  return isValid ? VALID : message;
};

export const validSGID = message => value => {
  return value.length === 9 ? VALID : message;
};

// Pasa también `allValues`: un validador puede depender de otro campo del
// formulario (ver `validSpanishTaxIdUnless`).
export const composeValidators = (...validators) => (value, allValues) =>
  validators.reduce((error, validator) => error || validator(value, allValues), VALID);
